import type { Perms, Role } from '../../types'

/**
 * What signing in produces, whichever database is behind it.
 *
 * The shape is deliberately the same as what AuthContext already held, so the
 * screens keep working unchanged while the two stacks run side by side.
 */
export interface Session {
  uid: string
  email: string
  username: string
  displayName?: string
  role: Role
  /**
   * What this person may do beyond their role. Absent or empty both mean
   * nothing extra was granted.
   */
  perms?: Perms
  /**
   * True when this account administers the whole system rather than one
   * institution.
   *
   * It is the difference between the person who creates institutions and
   * appoints their owners, and the owner of a single institution who knows
   * nothing of the others.
   */
  isSuperadmin: boolean
}

/**
 * Why a session ended or failed to start.
 *
 * Kept apart from the message shown on screen because the caller has to act
 * differently: a wrong PIN invites another try, a removed account does not,
 * and a dead connection must never look like either, a till that lost signal
 * mid-service should not be sent to the login screen, which needs the network.
 */
export type AuthFailure =
  | 'bad-credentials'
  | 'not-confirmed'
  | 'closed'
  | 'not-a-member'
  | 'offline'
  | 'rate-limited'
  | 'no-tenant'
  | 'not-configured'
  | 'unknown'

export class AuthError extends Error {
  constructor(
    readonly failure: AuthFailure,
    message: string,
    /**
     * Short, stable code shown beside the message.
     *
     * Exists so someone at a counter can read four characters down a phone
     * instead of describing a screen. Never reused once assigned, see
     * messages.ts.
     */
    readonly code?: string,
  ) {
    super(message)
    this.name = 'AuthError'
  }
}

/**
 * The operations AuthContext needs, with the database kept out of sight.
 *
 * Membership is checked at sign-in rather than left to the screens. Proving
 * the password is not proving the right to be here: an account removed from
 * an institution keeps working as a login, and without this check it lands on
 * a till that can read nothing, every screen saying "could not load", which
 * reads as a broken connection and sends people to check the wifi.
 */
export interface SignInResult {
  isSuperadmin: boolean
}

export interface AuthBackend {
  /** Resolves the current session, or null. Fires again on every change. */
  subscribe(
    tenantId: string | null,
    onSession: (session: Session | null) => void,
  ): () => void

  /**
   * Throws AuthError on failure. Says whether the account is the system
   * administrator's, so the login screen can take them to their panel rather
   * than into an institution's till.
   */
  signIn(tenantId: string, username: string, pin: string): Promise<SignInResult | void>

  /**
   * Mails a reset link. Owners only, staff addresses are synthetic and
   * nothing would arrive.
   */
  sendPasswordReset?(email: string): Promise<void>

  signOut(): Promise<void>
}
